/**
 * InvoicesDB — the "Invoices" master database on the Search page: Elite's
 * OWN outgoing invoices. Same layout language as the vendor tabs
 * (Crossroads/Hilti), but the source is QuickBooks ("Scan QB" date-range
 * pull) or the invoice PDFs themselves ("Import PDFs"). Columns: P.O.,
 * invoice #, customer (bill to), date, due date, subtotal, tax, total.
 * Duplicates by invoice # are never created (scan/import auto-skip).
 */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileUp, Loader2, Trash2 } from "lucide-react";
import api from "@/lib/api";
import ScanQBButton from "./ScanQBButton";

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const TH = "px-2.5 py-1.5 text-left font-mono text-[9px] uppercase tracking-widest text-white/50";
const TD = "px-2.5 py-1.5 text-xs text-white/85";

export default function InvoicesDB() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [sel, setSel] = useState(() => new Set());
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const fileRef = useRef(null);

  const load = async (silent = true) => {
    try {
      const { data } = await api.get("/master/out-invoices");
      setInvoices(data);
    } catch {
      if (!silent) toast.error("Could not load the invoices database");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(false); }, []);

  const onImport = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    const fd = new FormData();
    [...files].forEach((f) => fd.append("files", f));
    try {
      const { data } = await api.post("/master/out-invoices/import", fd);
      (data.results || []).forEach((r) => {
        if (!r.ok) toast.error(`${r.file}: ${r.error}`);
        else if (r.duplicate) toast.info(`${r.file}: invoice ${r.invoice_number} already in the table — skipped`);
        else toast.success(`${r.file}: invoice ${r.invoice_number} imported`);
      });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const toggleSel = (id) =>
    setSel((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const deleteInvoice = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.invoice_number} (${inv.customer || "no customer"}) from the invoices table?`)) return;
    try {
      await api.delete(`/master/out-invoices/${inv.invoice_id}`);
      toast.success("Invoice deleted");
      await load();
    } catch {
      toast.error("Could not delete it");
    }
  };

  const bulkDelete = async () => {
    const ids = [...sel];
    if (!ids.length) return;
    setBulkDeleting(true);
    try {
      const { data } = await api.post("/master/out-invoices/bulk-delete", { invoice_ids: ids });
      toast.success(`Deleted ${data.deleted} invoice${data.deleted === 1 ? "" : "s"}`);
      setSel(new Set());
      setConfirmBulk(false);
      await load();
    } catch {
      toast.error("Bulk delete failed");
    } finally {
      setBulkDeleting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="master-db-invoices">
      {/* header + actions */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
          Invoices · master database · our QuickBooks invoices
        </p>
        <div className="flex items-center gap-2">
          <ScanQBButton onDone={load} />
          <input ref={fileRef} type="file" accept=".pdf,application/pdf" multiple className="hidden"
            onChange={(e) => onImport(e.target.files)} data-testid="invoices-import-input" />
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
            data-testid="invoices-import-btn"
            className="flex items-center gap-2 rounded-md bg-[#1a3fd1] px-4 py-2 font-mono text-[11px] font-semibold uppercase tracking-widest text-white hover:bg-[#1633a3] disabled:opacity-50">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}
            {uploading ? "Importing…" : "Import PDFs"}
          </button>
        </div>
      </div>

      {loading && <p className="text-xs text-white/40" data-testid="invoices-loading">Loading…</p>}

      {/* invoices table */}
      <section>
        <div className="mb-2 flex items-center justify-between gap-2">
          <h2 className="font-mono text-xs font-semibold uppercase tracking-widest text-white/70" data-testid="invoices-heading">
            Invoices
            <span className="ml-2 text-white/35">{invoices.length}</span>
          </h2>
          {invoices.length > 0 && (
            <div className="flex items-center gap-3">
              <label className="flex cursor-pointer items-center gap-1.5 text-[11px] font-medium text-white/55">
                <input type="checkbox" className="h-3.5 w-3.5 accent-[#1a3fd1]"
                  checked={sel.size === invoices.length && invoices.length > 0}
                  onChange={() => setSel(sel.size === invoices.length ? new Set() : new Set(invoices.map((i) => i.invoice_id)))}
                  data-testid="invoices-select-all" />
                All
              </label>
              {sel.size > 0 && (
                <button type="button" onClick={() => setConfirmBulk(true)}
                  data-testid="invoices-delete-selected"
                  className="flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-red-700">
                  <Trash2 className="h-3.5 w-3.5" /> Delete ({sel.size})
                </button>
              )}
            </div>
          )}
        </div>

        {invoices.length === 0 && !loading && (
          <p className="rounded-lg border border-white/10 px-3 py-8 text-center text-xs text-white/35" data-testid="invoices-empty">
            No invoices yet — use Scan QB to pull a date range from QuickBooks, or Import PDFs.
          </p>
        )}

        {invoices.length > 0 && (
          <div className="overflow-x-auto rounded-lg border border-white/10">
            <table className="w-full min-w-[760px] border-collapse" data-testid="invoices-table">
              <thead>
                <tr className="bg-white/5">
                  <th className={TH}></th>
                  <th className={TH}>P.O. number</th>
                  <th className={TH}>Invoice #</th>
                  <th className={TH}>Customer</th>
                  <th className={TH}>Date</th>
                  <th className={TH}>Due date</th>
                  <th className={`${TH} text-right`}>Subtotal</th>
                  <th className={`${TH} text-right`}>Tax</th>
                  <th className={`${TH} text-right`}>Total</th>
                  <th className={TH}></th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.invoice_id} className="border-t border-white/5 hover:bg-white/[0.03]"
                    data-testid={`invoice-row-${inv.invoice_id}`}>
                    <td className={TD}>
                      <input type="checkbox" className="h-3.5 w-3.5 accent-[#1a3fd1]"
                        checked={sel.has(inv.invoice_id)} onChange={() => toggleSel(inv.invoice_id)}
                        data-testid={`invoice-sel-${inv.invoice_id}`} aria-label="Select invoice" />
                    </td>
                    <td className={`${TD} max-w-[160px] truncate`} title={inv.po_number} data-testid={`invoice-po-${inv.invoice_id}`}>{inv.po_number || "—"}</td>
                    <td className={`${TD} font-semibold text-white`} data-testid={`invoice-num-${inv.invoice_id}`}>{inv.invoice_number || "—"}</td>
                    <td className={`${TD} max-w-[180px] truncate`} title={inv.customer}>{inv.customer || "—"}</td>
                    <td className={TD}>{inv.invoice_date || "—"}</td>
                    <td className={TD}>{inv.due_date || "—"}</td>
                    <td className={`${TD} text-right`}>{fmtMoney(inv.subtotal)}</td>
                    <td className={`${TD} text-right`}>{fmtMoney(inv.tax)}</td>
                    <td className={`${TD} text-right font-semibold text-[#fb923c]`}>{fmtMoney(inv.total)}</td>
                    <td className={`${TD} text-right`}>
                      <span className="mr-2 font-mono text-[9px] uppercase tracking-wider text-white/30">
                        {inv.source === "quickbooks" ? "QB" : "PDF"}
                      </span>
                      <button type="button" onClick={() => deleteInvoice(inv)}
                        data-testid={`invoice-del-${inv.invoice_id}`} aria-label="Delete invoice"
                        className="rounded p-1 text-white/25 hover:bg-red-500/15 hover:text-red-400">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* bulk delete confirmation popup */}
      {confirmBulk && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/80 p-4"
          onClick={() => !bulkDeleting && setConfirmBulk(false)}>
          <div className="w-full max-w-sm rounded-xl border border-white/15 bg-[#0d0d0d] p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()} data-testid="invoices-bulk-confirm-dialog">
            <h3 className="text-sm font-bold text-white">
              Delete {sel.size} invoice{sel.size === 1 ? "" : "s"}?
            </h3>
            <p className="mt-2 text-xs text-white/50">
              This permanently removes {sel.size === 1 ? "it" : "them"} from the invoices master database. This cannot be undone.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setConfirmBulk(false)} disabled={bulkDeleting}
                data-testid="invoices-bulk-cancel"
                className="rounded-md border border-white/15 px-4 py-2 text-xs font-semibold text-white/60 hover:text-white">
                Cancel
              </button>
              <button type="button" onClick={bulkDelete} disabled={bulkDeleting}
                data-testid="invoices-bulk-confirm"
                className="flex items-center gap-2 rounded-md bg-red-600 px-4 py-2 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50">
                {bulkDeleting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {bulkDeleting ? "Deleting…" : "Delete permanently"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
